import React, { useState } from 'react';
import { DIAGNOSTIC_QUESTIONS, CEFR_LEVELS } from '../data/diagnosticQuestions';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

const DiagnosticTest = ({ user, onComplete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [calculatedLevel, setCalculatedLevel] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleAnswer = (selectedOption) => {
    const currentQuestion = DIAGNOSTIC_QUESTIONS[currentIndex];
    
    // Simple scoring: +1 for correct. 
    // *Advanced Logic Idea:* You could weight harder questions (C1) higher than A1.
    let newScore = score;
    if (selectedOption === currentQuestion.correctAnswer) {
      newScore = score + 1;
    }
    setScore(newScore);

    const nextIndex = currentIndex + 1;
    if (nextIndex < DIAGNOSTIC_QUESTIONS.length) {
      setCurrentIndex(nextIndex);
    } else {
      finishTest(newScore);
    }
  };

  const finishTest = async (finalScore) => {
    // Calculate Percentage
    const percentage = (finalScore / DIAGNOSTIC_QUESTIONS.length) * 100;
    
    // Determine Level based on CEFR_LEVELS thresholds
    // We reverse the array to find the highest matching threshold
    const levelData = [...CEFR_LEVELS].reverse().find(l => percentage >= l.score) || CEFR_LEVELS[0];
    
    setCalculatedLevel(levelData);
    setShowResult(true);
    setIsSaving(true);

    // ✅ AUTOMATION: Save result to Firebase User Profile
    if (user) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          cefrLevel: levelData.label,
          diagnosticScore: percentage,
          diagnosticDate: new Date()
        });
        setIsSaving(false);
        if (onComplete) onComplete(); // Tell dashboard to refresh/unlock
      } catch (error) {
        console.error("Error saving level:", error);
        setIsSaving(false);
      }
    }
  };

  if (showResult) {
    return (
      <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-lg mx-auto">
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Diagnosis Complete!</h2>
        <p className="text-gray-600 mb-6">Based on your answers, your current standing is:</p>
        
        <div className="bg-indigo-50 p-6 rounded-xl border-2 border-indigo-100 mb-6">
          <h1 className="text-4xl font-extrabold text-indigo-700">{calculatedLevel.label}</h1>
          <p className="text-sm text-indigo-500 mt-2">{calculatedLevel.description}</p>
        </div>

        {isSaving ? (
          <p className="text-sm text-gray-400">Saving results to your profile...</p>
        ) : (
          <button 
            onClick={() => window.location.reload()} 
            className="bg-indigo-600 text-white px-6 py-3 rounded-full font-bold hover:bg-indigo-700 transition"
          >
            Go to My Dashboard
          </button>
        )}
      </div>
    );
  }

  const question = DIAGNOSTIC_QUESTIONS[currentIndex];
  const progress = ((currentIndex + 1) / DIAGNOSTIC_QUESTIONS.length) * 100;

  return (
    <div className="max-w-2xl mx-auto bg-white p-8 rounded-xl shadow">
      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
        <div className="bg-indigo-600 h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
      </div>

      <div className="mb-2 text-xs font-bold text-indigo-500 uppercase tracking-wide">
        Question {currentIndex + 1} of {DIAGNOSTIC_QUESTIONS.length} • Difficulty: {question.level}
      </div>

      <h3 className="text-xl font-bold text-gray-900 mb-6">{question.question}</h3>

      <div className="space-y-3">
        {question.options.map((option, idx) => (
          <button
            key={idx}
            onClick={() => handleAnswer(option)}
            className="w-full text-left p-4 border rounded-lg hover:bg-indigo-50 hover:border-indigo-300 transition-all group"
          >
            <span className="inline-block w-6 h-6 bg-gray-100 text-gray-500 rounded-full text-center text-sm leading-6 mr-3 group-hover:bg-indigo-600 group-hover:text-white">
              {String.fromCharCode(65 + idx)}
            </span>
            {option}
          </button>
        ))}
      </div>
    </div>
  );
};

export default DiagnosticTest;